import React, { useState } from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { canAskAI, incrementAICount, isPro } from '../utils/featureGate';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';

// ─────────────────────────────────────────────────────────────────────────────
// COURSE DATA — Fact-checked, 2025-current, written for total beginners
// Sources: IRS.gov, Fidelity, Vanguard, Federal Reserve, Investopedia, Bankrate
// ─────────────────────────────────────────────────────────────────────────────
export const COURSES = [
  {
    id: '401k',
    icon: '🏦',
    title: '401(k) Plans',
    tagline: 'The most powerful retirement tool most people never fully use.',
    color: '#38bdf8',
    lessons: [
      {
        title: 'What Is a 401(k)? (Start Here)',
        content: `If you're brand new to this, take a breath — the 401(k) sounds complicated but the basic idea is really simple.

A 401(k) is a retirement savings account that your employer sets up for you. The name comes from a section of the US tax code (literally Section 401, paragraph k). You put money into it from each paycheck — and here's the key part — you get a tax break for doing it.

Here's the simplest way to think about it:

Normally, if you earn $50,000 per year, the government taxes all $50,000. But if you put $5,000 into your 401(k), you only get taxed on $45,000. That $5,000 is sitting in your retirement account, growing tax-free until you retire. Only when you take the money out in retirement do you pay taxes on it.

Why does that matter? Two big reasons:

1. You get to invest money that would have gone to taxes right now. More money working for you, sooner.
2. By retirement, you may be in a lower tax bracket (you're not working anymore, so less income), meaning you'll pay less tax when you eventually withdraw.

The 401(k) is not a savings account that just sits there. It's an investment account. Your money is invested in stocks, bonds, and funds that grow over time. The average annual return of the S&P 500 (a common benchmark) has been about 10% per year over the last century.

Bottom line: A 401(k) is a tax-advantaged retirement savings account provided by your employer. If you have access to one — use it.`,
        takeaway: 'A 401(k) reduces your taxable income NOW and lets your money grow tax-deferred until retirement.',
      },
      {
        title: 'The Employer Match — This Is Free Money',
        content: `This is the single most important thing to understand about your 401(k): the employer match. It is literally free money that you earn just by participating.

Here's how it works. Many employers say something like: "We'll match 50% of your contributions, up to 6% of your salary."

Let's break that down with real numbers:
- Your salary: $60,000
- 6% of your salary: $3,600/year
- You contribute $3,600 to your 401(k)
- Your employer adds 50% of that: $1,800
- Total invested: $5,400 — but you only put in $3,600

Your employer just gave you $1,800 you didn't have to earn.

Now, some employers offer a full match: "We'll match 100% of contributions up to 4%." Even better. That's a 100% instant return on your investment before it even grows.

If your employer offers a match and you're NOT contributing enough to get the full match, you are leaving part of your salary on the table. This is the most common retirement mistake people make. Not investing in the stock market and losing a little is one thing. Refusing free money from your employer is another entirely.

What if I don't have a match? Many smaller employers don't offer one. That's okay — a 401(k) still gives you the tax break, which is valuable. But the match is the cherry on top.

When does the match fully belong to me? Watch out for "vesting." Your employer may require you to stay a certain number of years before their match money is fully yours. Check your plan's vesting schedule.

Action step: Log into your HR portal today and check: (1) Does your employer offer a match? (2) What's the formula? (3) Are you contributing enough to get the full match?`,
        takeaway: "Always contribute at least enough to capture your full employer match — it's an instant 50-100% return on that money.",
      },
      {
        title: '2025 Contribution Limits (Exactly How Much You Can Contribute)',
        content: `The IRS sets limits on how much you can put into your 401(k) each year. Here are the official 2025 limits:

Under age 50:
→ Maximum contribution: $23,500 per year
→ This works out to about $1,958/month or $902/paycheck (biweekly)

Age 50 or older (catch-up contributions):
→ Maximum: $23,500 + $7,500 = $31,000 per year
→ The extra $7,500 is called a "catch-up contribution" — it's designed for people who got a late start saving

Age 60–63 (new SECURE 2.0 "super catch-up"):
→ As of 2025, thanks to the SECURE 2.0 Act, people aged 60–63 get an even bigger catch-up: $11,250 instead of $7,500
→ Maximum total: $23,500 + $11,250 = $34,750 per year

Don't panic if you can't hit these limits. Most people don't. The goal isn't to max it out on day one — the goal is to contribute consistently over time. Even 5–10% of your paycheck is a fantastic start.

Here's a real-world example of what consistent contributing looks like:
- You're 30 years old
- You contribute $400/month ($4,800/year)
- Your employer matches $200/month ($2,400/year)
- Total invested: $600/month
- At a 7% average return over 35 years → you retire with roughly $1 million

The math is on your side if you start early. A 25-year-old who invests $200/month will have far more at retirement than a 45-year-old who invests $800/month — because of compound growth.`,
        takeaway: '2025 limit: $23,500 (under 50), $31,000 (50+), $34,750 (ages 60–63). Start small and increase over time.',
      },
      {
        title: 'Traditional 401(k) vs. Roth 401(k) — Which Is Better?',
        content: `Many 401(k) plans now offer two flavors: Traditional and Roth. Understanding the difference can save you thousands in taxes over your lifetime.

Traditional 401(k):
- You contribute PRE-TAX dollars (money before the IRS takes their cut)
- Your taxable income goes DOWN this year — you pay less tax now
- Your investments grow tax-deferred
- When you withdraw in retirement, you pay income tax on everything you take out

Roth 401(k):
- You contribute AFTER-TAX dollars (the IRS already took their cut)
- No tax break today — your taxable income stays the same
- Your investments grow completely tax-FREE
- When you withdraw in retirement: ZERO TAXES on the entire amount, including all the growth

Here's the key question: Will you be in a higher or lower tax bracket in retirement than you are today?

If you're early in your career (lower income now), a Roth 401(k) is usually better. You pay taxes at your current lower rate, then never pay taxes again — even when your $10,000 contribution grows to $80,000.

If you're at peak earnings (high income now), a Traditional 401(k) is usually better. You get a big tax deduction today at your high rate, and might withdraw in retirement at a lower rate.

A simple rule of thumb used by many financial educators:
- Early career / lower income → Roth 401(k)
- Mid-to-late career / high income → Traditional 401(k)
- Unsure? → Split contributions between both. Many plans allow this.

Important note: The contribution limits ($23,500 for 2025) apply to the TOTAL of both types combined. You can split however you like, but can't exceed the total limit.`,
        takeaway: 'Roth = pay taxes now, withdraw tax-free. Traditional = pay taxes later. Younger/lower-income earners often benefit more from Roth.',
      },
      {
        title: 'What to Invest In Inside Your 401(k)',
        content: `This is where a lot of people freeze up. Your 401(k) account is open — now what do you actually pick?

Most 401(k) plans offer a menu of options, usually including:

Target-Date Funds (The "Set It and Forget It" Option):
These are the best starting point for most beginners. You pick the fund closest to the year you plan to retire — like "Target Date 2055 Fund" if you're planning to retire around 2055. The fund automatically adjusts its investments as you age: more aggressive (more stocks) when you're young, more conservative (more bonds) as you approach retirement. You put your money in and leave it. Done.

Index Funds:
An index fund holds every stock in a particular index, like the S&P 500. Instead of trying to pick winners, you own a tiny piece of every major company. Historically, index funds outperform the majority of actively managed funds over long periods. Look for ones with low expense ratios (under 0.20% is great).

Expense Ratios — The Hidden Cost:
Every fund has an annual fee called an expense ratio. A 0.05% expense ratio on a $100,000 account costs $50/year. A 1.5% expense ratio costs $1,500/year. Over 30 years, that difference is enormous — potentially $200,000+ in lost growth. Always choose the lowest-cost option that fits your strategy.

What to avoid as a beginner:
- Individual company stocks (putting all eggs in one basket)
- Actively managed funds with expense ratios above 0.5%
- Anything you don't understand

Practical starting point: If your plan has a target-date fund close to your retirement year, that's a solid default. Once you're comfortable, you can explore index funds.`,
        takeaway: 'Target-date funds are the simplest starting point. Look for low expense ratios — fees quietly drain your returns over decades.',
      },
      {
        title: 'What Happens to Your 401(k) When You Leave a Job?',
        content: `People change jobs all the time, and this question comes up constantly. The good news: your 401(k) money is yours.

Option 1 — Roll it to your new employer's 401(k):
Your new company probably has its own 401(k) plan. You can move your old 401(k) balance directly into the new one. This keeps everything in one place and may give you access to better investment options.

Option 2 — Roll it to an IRA (Often the Best Move):
You can move the money into an Individual Retirement Account (IRA) at a broker like Fidelity, Vanguard, or Schwab. IRAs typically offer MORE investment choices than employer plans — including low-cost index funds and ETFs. This is what many financial educators recommend.

Option 3 — Leave it with your old employer:
If the plan is good (low fees, good investment options), you can leave it there. But you'll lose the ability to contribute, and it's easy to forget about.

Option 4 — Cash It Out (AVOID THIS):
This seems tempting but it's a retirement disaster. If you withdraw before age 59½, you'll owe:
- Income tax on the entire amount (could be 22-24% or more)
- A 10% early withdrawal penalty

Example: You have $20,000 in your 401(k). You cash out. You might walk away with only $13,000-$14,000 after taxes and penalties — and you've permanently lost decades of compound growth on that money.

The right process for rolling over: Call your new broker (Fidelity, Vanguard, etc.), tell them you want to do a "direct rollover." They handle the paperwork. The money moves directly from one account to another, and you pay zero taxes.`,
        takeaway: "When you leave a job, roll your 401(k) to an IRA or new employer plan. Never cash it out — you'll lose 30-40% to taxes and penalties.",
      },
    ]
  },
  {
    id: 'iras',
    icon: '💰',
    title: 'IRAs — All Types Explained',
    tagline: 'The retirement accounts you control — independent of any employer.',
    color: '#a78bfa',
    lessons: [
      {
        title: 'What Is an IRA? (The Big Picture)',
        content: `IRA stands for Individual Retirement Account. The key word is "Individual" — you open this yourself, not through an employer. You control it completely, no matter where you work.

Think of an IRA as a special wrapper around your investments. The money inside gets tax advantages — either now or later, depending on which type you choose. Without an IRA, if you invest in a regular brokerage account, you pay capital gains taxes every time you sell something at a profit. Inside an IRA, that tax friction disappears.

There are four main types of IRAs:
1. Traditional IRA — Tax deduction now, pay taxes in retirement
2. Roth IRA — Pay taxes now, tax-FREE forever after
3. SEP IRA — For self-employed people and small business owners
4. SIMPLE IRA — For small businesses with employees

You can have multiple types. For example, you could have a 401(k) through work AND a Roth IRA you opened yourself. Many people do exactly this for maximum tax diversification.

For 2026, the contribution limit for both Traditional and Roth IRAs is $7,500 per year ($8,600 if you're 50 or older). This is the combined limit — you can't put $7,000 in a Traditional AND $7,000 in a Roth in the same year.

Why open an IRA if you already have a 401(k)?
- More investment choices (IRAs let you invest in virtually anything)
- Often lower fees
- Different tax treatment for additional tax diversification
- Roth IRA money can be withdrawn any time, penalty-free (your contributions, not earnings)

Bottom line: Almost every working person benefits from having an IRA in addition to — or instead of — a 401(k).`,
        takeaway: "An IRA is a retirement account you control. $7,500/year contribution limit in 2026. You can have one alongside a 401(k).",
      },
      {
        title: 'Traditional IRA — The Tax Break You Can Use Right Now',
        content: `The Traditional IRA gives you a potential tax deduction in the year you contribute. This is valuable if your income is high enough that the deduction actually reduces what you owe.

How it works:
- You contribute up to $7,500 in 2026 ($8,600 if 50+)
- Depending on your situation, that contribution may be tax-deductible
- Your money grows tax-deferred (you don't pay taxes on dividends or gains each year)
- When you withdraw in retirement, you pay ordinary income tax on withdrawals

The catch — Required Minimum Distributions (RMDs):
Once you turn 73 (as of 2023 rules), the IRS requires you to start withdrawing a minimum amount each year, whether you need the money or not. They want their tax money eventually. If you miss an RMD, the penalty is steep.

Is the contribution tax-deductible?
This depends on two things: (1) whether you or your spouse have a workplace retirement plan, and (2) your income.

If neither you NOR your spouse has a workplace plan: Your contribution is always fully deductible, regardless of income.

If you have a workplace plan (like a 401k): In 2026, the deduction phases out for single filers earning $81,000–$91,000 and married-filing-jointly earning $130,000–$150,000. Above those limits, no deduction.

But here's the thing: even if your contribution isn't deductible (called a "non-deductible Traditional IRA"), your money still grows tax-deferred. It's still better than a regular taxable brokerage account.

Best for: People who expect to be in a lower tax bracket in retirement than they are today, or those who need the tax deduction right now.`,
        takeaway: '2026 limit: $7,500 ($8,600 if 50+). Contributions may be tax-deductible. Pay taxes when you withdraw in retirement. RMDs start at age 73.',
      },
      {
        title: 'Roth IRA — The Single Best Investment Account for Most People',
        content: `Financial educators almost universally call the Roth IRA one of the greatest wealth-building tools ever created. Here's why.

The core deal: You contribute after-tax money. Your investments grow completely tax-free. You pay ZERO taxes when you withdraw in retirement — on contributions OR growth.

Let's see that with real numbers:
You put $7,000 into a Roth IRA at age 25. You invest it in an S&P 500 index fund. At an average 10% annual return, by age 65 that $7,000 has grown to approximately $316,000. Under traditional investing, you'd pay capital gains taxes as it grew. With the Roth IRA? You get to keep all $316,000. Zero taxes.

And here's the bonus no one tells beginners: you can take out your CONTRIBUTIONS (not the growth) any time, for any reason, with zero taxes and zero penalties. This makes the Roth IRA also work as a flexible emergency fund for your contributions.

2025 Income Limits (You Must Qualify):
Unlike a Traditional IRA, the Roth IRA has income limits. In 2025:
- Single filers: Full contribution if income is under $150,000. Phases out between $150,000–$165,000. No contribution above $165,000.
- Married filing jointly: Full contribution under $236,000. Phases out $236,000–$246,000. No contribution above $246,000.

What if I earn too much? There's a legal workaround called the "Backdoor Roth IRA." You contribute to a non-deductible Traditional IRA and then convert it to a Roth. This is a legitimate strategy used by high earners.

No Required Minimum Distributions: Unlike Traditional IRAs and 401(k)s, you NEVER have to withdraw from a Roth IRA during your lifetime. You can let it grow and pass it to your heirs.

Best for: Anyone under 50 in a low-to-moderate tax bracket, anyone who wants tax-free retirement income, anyone who wants flexibility.`,
        takeaway: 'Roth IRA = tax-free growth forever. 2025 income limit: $150K single / $236K married. No RMDs. Best account for most earners under 50.',
      },
      {
        title: 'SEP IRA — The Power Move for Self-Employed & Freelancers',
        content: `If you're self-employed, a freelancer, a contractor, or a small business owner — the SEP IRA is one of the most powerful retirement tools available to you.

SEP stands for Simplified Employee Pension. It works similarly to a Traditional IRA — contributions are tax-deductible, money grows tax-deferred, and you pay taxes when you withdraw in retirement.

What makes the SEP IRA special is the contribution limit. While a regular IRA caps you at $7,000, the SEP IRA allows you to contribute:
- Up to 25% of your net self-employment income, OR
- $70,000 maximum for 2025 (whichever is less)

Real example:
You're a freelance designer who made $120,000 net income. 25% of $120,000 = $30,000. You can put $30,000 into your SEP IRA and deduct the entire amount from your taxes. At a 22% tax rate, that's $6,600 in taxes saved immediately — plus decades of tax-deferred growth.

Compare that to a regular IRA at $7,000. The SEP IRA is a retirement turbo boost for the self-employed.

If you have employees: You must contribute the same percentage of compensation to all eligible employees. So if you contribute 20% of your own income, you must also contribute 20% for each qualifying employee. This is a key planning consideration if you hire people.

No employee salary deferrals: Unlike a 401(k), only the employer (you) makes contributions to a SEP IRA. Your employees can't contribute their own money. If you want employees to be able to contribute their own money, a SIMPLE IRA or Solo 401(k) may be better.

Easy to set up: You can open a SEP IRA at any major broker (Fidelity, Vanguard, Schwab) in minutes online. You have until your tax filing deadline (including extensions) to make contributions for the prior year.`,
        takeaway: 'Self-employed? The SEP IRA lets you contribute up to $70,000/year (2025) — far more than a regular IRA. Tax-deductible contributions.',
      },
      {
        title: 'SIMPLE IRA — For Small Businesses with Employees',
        content: `The SIMPLE IRA (Savings Incentive Match Plan for Employees) is designed for small businesses with 100 or fewer employees. It's easier and less expensive to administer than a 401(k), making it a popular choice for small business owners who want to offer retirement benefits.

How it works — employees contribute:
Unlike a SEP IRA, employees can contribute their own money via payroll deductions. For 2025, employees can contribute up to $16,500 per year ($20,000 if age 50 or older). In 2025, a new "super catch-up" applies for employees aged 60–63: up to $21,750.

Employer must contribute:
The employer is required to make contributions using one of two formulas:
Option A: Match employee contributions dollar-for-dollar, up to 3% of their salary
Option B: Make a flat 2% non-elective contribution for all eligible employees (whether or not they contribute)

Real example (Option A):
Employee earns $50,000 and contributes 3% ($1,500). Employer matches 3% ($1,500). Total going into their retirement: $3,000/year. Employee only "spent" $1,500.

SIMPLE IRA vs. 401(k):
The SIMPLE IRA has lower contribution limits and less flexibility than a 401(k), but it's much simpler (hence the name) and cheaper to maintain — typically no plan administration fees. For a small business owner who wants to offer something without the complexity of a full 401(k), it's an excellent option.

One restriction: With a SIMPLE IRA, you can't also have another retirement plan (like a SEP IRA) in the same year for the same business.`,
        takeaway: 'SIMPLE IRA: employee limit $17,000 in 2026. Employers must match or make flat 2% contribution. Great for small businesses that want to offer retirement benefits affordably.',
      },
      {
        title: 'IRA vs. 401(k): Which Do You Use First?',
        content: `Here's the question most beginners have: "I have limited money. Should I put it in my 401(k) or an IRA?"

The answer most financial educators agree on — follow this order:

Step 1 — Get the free employer match first
If your employer offers a 401(k) match, contribute enough to get the FULL match before doing anything else. That's an instant 50–100% return. Nothing else beats it.

Step 2 — Max out your Roth IRA
If you qualify (income under $153,000 single), put your next $7,000 into a Roth IRA. Why? Because the Roth IRA typically offers more investment choices and better flexibility than a 401(k). The tax-free growth is also hard to beat.

Step 3 — Go back to your 401(k)
Once you've maxed the Roth IRA, direct additional savings back to your 401(k) up to the $23,500 limit.

Step 4 — Taxable brokerage account
Once both are maxed, invest additional savings in a regular brokerage account.

Why this order matters:
Each account type has different tax advantages. By diversifying across account types (pre-tax 401k, post-tax Roth IRA), you give yourself flexibility in retirement to draw income in the most tax-efficient way possible.

What if I can only do one?
If you can only do one and your employer offers no match, many educators recommend the Roth IRA for most people under 40 — because the tax-free growth over decades is often worth more than the deduction today.

If your employer offers a match, always capture that first — it's the highest guaranteed return you'll ever find.`,
        takeaway: 'Priority order: (1) Get full employer match, (2) Max Roth IRA, (3) Max 401(k), (4) Taxable accounts. Match first — always.',
      },
    ]
  },
  {
    id: 'stocks',
    icon: '📈',
    title: 'Stock Market Basics',
    tagline: 'What stocks actually are, how the market works, and how to start.',
    color: '#22c55e',
    lessons: [
      {
        title: 'What Is a Stock? (Seriously Simple)',
        content: `A stock is ownership. That's it.

When a company wants to raise money to grow its business, it can sell tiny pieces of ownership to the public. Each piece is called a share. When you buy a share of a company, you literally become a part-owner of that company — no matter how small the slice.

Here's a concrete example: Apple Inc. has roughly 15 billion shares outstanding. If you buy 100 shares of Apple, you own about 0.0000007% of Apple — a tiny sliver, sure, but you're an actual owner. You're entitled to a proportional share of Apple's profits (through dividends), and your shares rise in value as Apple's business grows.

Why do stocks go up in value?
Because as a company earns more money, becomes more valuable, or investors expect it to grow more in the future — demand for its shares increases. More demand → higher price.

Why do stocks go down?
Bad earnings, bad news, rising interest rates, economic fears, or simply investors losing confidence and selling. When more people want to sell than buy, prices fall.

The two ways you make money from stocks:
1. Price appreciation: You buy at $50, sell at $100. $50 profit per share.
2. Dividends: Some companies pay shareholders a portion of their profits, usually quarterly. You get paid just for holding the stock — even if the price doesn't move.

One important reality: Individual stocks are risky. Any single company can fail — think Enron, Blockbuster, Lehman Brothers. This is why most beginners are better served by buying groups of stocks (through index funds or ETFs) rather than betting on individual companies.`,
        takeaway: 'A stock = a tiny ownership stake in a company. You profit when the company grows (price up) or when it pays dividends.',
      },
      {
        title: 'What Is the Stock Market? And What Is the S&P 500?',
        content: `The stock market is not a physical place (anymore). It's a global network where buyers and sellers trade ownership in companies electronically. In the US, the two main exchanges are the New York Stock Exchange (NYSE) and the Nasdaq.

Here's what's wild: over 10,000 stocks trade in the US markets. How do you make sense of it all?

That's where indexes come in.

An index is a collection of stocks that represents a specific slice of the market. Think of it like a leaderboard or a report card for a group of companies.

The S&P 500:
The most important index in the world. It tracks the 500 largest publicly traded companies in the United States — companies like Apple, Microsoft, Amazon, Google, JPMorgan, and Johnson & Johnson. The S&P 500 is widely considered "the market" because these 500 companies represent about 80% of total US stock market value.

Historical performance: The S&P 500 has averaged approximately 10% per year since 1957. Over the last 40 years (as of late 2025), Fidelity data shows an average annual return of about 11.5%.

The Dow Jones Industrial Average (DJIA):
Tracks 30 large, well-established US companies. It's the oldest and most widely cited index, but it's actually less representative of the overall market than the S&P 500 because it only has 30 companies.

The Nasdaq Composite:
Tracks over 3,000 companies listed on the Nasdaq exchange. It's heavily weighted toward technology companies — Apple, Microsoft, Amazon, Meta, Google. It tends to be more volatile than the S&P 500.

The key insight: Over long periods, the broad stock market has consistently gone up — even accounting for crashes. Every single crash in history has eventually been followed by new all-time highs.`,
        takeaway: 'The S&P 500 is the most important market index — 500 of the biggest US companies, averaging ~10% annually over the long term.',
      },
      {
        title: 'Index Funds and ETFs — The Smartest Way for Most People to Invest',
        content: `Here's one of the most important truths in investing: most professional fund managers, after fees, do NOT consistently beat a simple S&P 500 index fund. SPIVA data shows that over 15 years, roughly 88% of actively managed US equity funds underperform their benchmark index.

So what does that mean for you? It means that buying a simple index fund is not just the easy choice — it's often the optimal choice.

What is an index fund?
An index fund holds every stock in a particular index. A S&P 500 index fund owns tiny amounts of all 500 companies in the S&P 500. When you buy one share of the fund, you instantly own a tiny piece of 500 of the biggest companies in America.

What is an ETF (Exchange-Traded Fund)?
An ETF works similarly to an index fund but trades on the stock exchange like a regular stock — you can buy or sell throughout the day. Most popular ETFs track indexes.

The best starting ETFs for beginners (widely recommended by educators):
- VOO (Vanguard S&P 500 ETF) — expense ratio: 0.03%/year
- SPY (SPDR S&P 500 ETF) — most traded ETF in the world
- VTI (Vanguard Total Stock Market ETF) — US entire market
- VXUS (Vanguard Total International) — international stocks

These funds charge almost nothing in fees (0.03% means you pay $3 per year on a $10,000 investment) and give you instant diversification.

Dollar-Cost Averaging (DCA):
This is the strategy of investing a fixed amount on a regular schedule — say, $200 every month — regardless of what the market is doing. When prices are high, you buy fewer shares. When prices are low, you automatically buy more. Over time, this averages out your cost basis and removes the stress of trying to time the market. It's the most beginner-friendly investing strategy that exists.`,
        takeaway: 'Index funds like VOO or VTI let you own 500+ companies at once with tiny fees. Dollar-cost averaging — investing a fixed amount regularly — beats trying to time the market.',
      },
      {
        title: 'How to Actually Read a Stock',
        content: `When you look up any stock, you'll see a wall of numbers. Here's what the most important ones actually mean:

Price: The current cost of one share. Nothing more.

Market Capitalization (Market Cap): Total value of the company = share price × total shares outstanding. Apple's market cap has exceeded $3 trillion, making it one of the most valuable companies ever. Market cap lets you understand the SIZE of a company:
- Large-cap: $10B+ (Apple, Microsoft) — generally stable
- Mid-cap: $2B–$10B — growth potential with moderate risk
- Small-cap: Under $2B — higher potential, higher risk

P/E Ratio (Price-to-Earnings): This is one of the most important valuation metrics. It tells you how much investors are willing to pay per dollar of profit.

P/E = Current Share Price ÷ Annual Earnings Per Share

A P/E of 20 means investors are paying $20 for every $1 of annual profit. A P/E of 30 might mean investors expect strong future growth. A P/E of 8 might mean the company is cheap — or there's a reason nobody wants it.

Historical average S&P 500 P/E is about 15–16. When it's much higher, markets are generally considered expensive. Much lower, potentially a bargain.

52-Week High/Low: The highest and lowest price the stock has traded in the past year. Useful for context — is it near a peak or near a valley?

Dividend Yield: Annual dividend payment ÷ current price, as a percentage. A 3% yield means if you own $10,000 of that stock, you get $300/year in dividends just for holding it.

Volume: How many shares traded today. High volume on a big price move = meaningful. Low volume = potentially noise.

EPS (Earnings Per Share): How much profit the company made per share. Growing EPS is a positive sign.`,
        takeaway: 'Key metrics: Price, Market Cap (size), P/E ratio (valuation), Dividend Yield (income), and EPS (profitability). Master these and you can evaluate any stock.',
      },
      {
        title: 'The Biggest Beginner Mistakes in the Stock Market',
        content: `Learning what NOT to do is just as important as learning what to do.

Mistake #1: Trying to "time the market"
This means waiting for the "right time" to buy or trying to sell before a crash. It sounds smart but virtually nobody does it successfully over the long term. The best time to invest is consistently, over time. As the saying goes: "Time in the market beats timing the market."

Proof: If you had invested $10,000 in the S&P 500 in 2003 and just left it alone through every crash and crisis, by 2023 you'd have roughly $72,000. If you tried to be clever and missed just the 10 best trading days, you'd have about $33,000. Missing 10 days in 20 years cost you $39,000.

Mistake #2: Panic selling during downturns
Markets drop. This is not an emergency — it's a normal feature of markets. The S&P 500 has experienced a 10% correction on average every 1-2 years. A 20%+ bear market happens every 3-5 years on average. Every single one has recovered to new highs.

Selling during a crash locks in your losses permanently. The people who kept buying during 2008-2009 and 2020 made life-changing returns.

Mistake #3: Over-concentrating in one stock or sector
Even great companies fail. Diversification isn't exciting, but it protects you.

Mistake #4: Letting emotions drive decisions
Fear and greed are the two biggest enemies of investment returns. Having a plan and sticking to it — regardless of headlines — is what separates successful long-term investors from everyone else.

Mistake #5: Waiting until you have "enough" money to start
You can buy fractional shares of major ETFs for as little as $1. There is no minimum. The biggest advantage you have is time. Starting with $50/month at 25 beats starting with $500/month at 45.`,
        takeaway: "The biggest mistake: panic selling or trying to time the market. Stay invested consistently. Time is your most powerful asset.",
      },
    ]
  },
  {
    id: 'crypto',
    icon: '₿',
    title: 'Cryptocurrency 101',
    tagline: 'No hype, no fear. Just honest facts about what crypto is.',
    color: '#f59e0b',
    lessons: [
      {
        title: 'What Is Cryptocurrency? (Plain English)',
        content: `Cryptocurrency is digital money that runs on a computer network instead of a bank or government.

Regular money — dollars, euros, yen — is controlled by governments and central banks. They decide how much to print, they control the financial system, and transactions flow through banks that can freeze your account or charge fees.

Cryptocurrency flips this. It runs on a decentralized network of computers around the world. No single person, government, or company controls it. Transactions happen directly between people, without a bank in the middle.

Think of it like this: Email let you send messages directly to anyone in the world without a postal service. Cryptocurrency lets you send money directly to anyone in the world without a bank.

Where did it come from?
Bitcoin was the first cryptocurrency, created in 2009 by a person (or group) under the pseudonym "Satoshi Nakamoto." The identity of Satoshi remains unknown to this day. Bitcoin was created as a response to the 2008 financial crisis — an attempt to create money that couldn't be controlled or manipulated by banks or governments.

How many cryptocurrencies are there?
As of 2025, there are over 20,000 different cryptocurrencies. The vast majority have little to no value or legitimate use. Bitcoin (BTC) and Ethereum (ETH) are by far the most established and most credible. The rest range from legitimate projects to outright scams.

Is it legal?
Yes, in most countries. In the US, crypto is legal to buy, hold, and sell. The IRS treats it as property for tax purposes, meaning every sale is a taxable event. El Salvador became the first country to make Bitcoin legal tender in 2021.

Should you buy crypto?
That's a personal decision that depends on your risk tolerance, financial situation, and goals. What matters is understanding what it is, how it works, and what the real risks are before putting any money in.`,
        takeaway: "Crypto = digital money on a decentralized computer network. No banks, no central control. Bitcoin was first (2009). Over 20,000 cryptos exist; most have little value.",
      },
      {
        title: 'How Blockchain Works (Without the Tech Jargon)',
        content: `Every cryptocurrency transaction is recorded on a blockchain. Understanding what a blockchain is helps you understand why crypto is valuable to some people — and why it's difficult to counterfeit or hack.

The simplest analogy:

Imagine a Google Spreadsheet that records every financial transaction ever made on the network. But instead of being stored on Google's servers, it's stored on 10,000+ computers around the world simultaneously. Every computer has the same identical copy. When a new transaction happens, it gets added to everyone's copy at the same time.

To "hack" or "counterfeit" a transaction, you'd have to change the records on more than half of all those computers simultaneously — and all within seconds, while the network is constantly updating. This is computationally and economically nearly impossible. That's what makes blockchain secure.

Why is it called a "blockchain"?
Transactions are grouped together into "blocks." Each block is mathematically linked to the previous block, creating a chain. Hence: blockchain. If you tried to change a past transaction, you'd break the mathematical link — the network would immediately reject it.

What are "miners" and why do they matter?
In Bitcoin's network, "miners" are computers that compete to verify and add new blocks of transactions to the blockchain. The winner gets newly created Bitcoin as a reward. This is how new Bitcoin is created and how transactions are verified without a central authority.

Mining requires enormous computing power and electricity. This is one of the most common criticisms of Bitcoin — its energy consumption. Bitcoin defenders argue that much mining runs on renewable energy, and that it's a fair trade for a truly decentralized, censorship-resistant monetary system.`,
        takeaway: "A blockchain is an unalterable shared record book stored on thousands of computers simultaneously. Transactions are nearly impossible to fake or reverse.",
      },
      {
        title: 'Bitcoin — Digital Gold or Speculative Bubble?',
        content: `Bitcoin is the original cryptocurrency and still by far the most important. Understanding it requires understanding both the bull and bear case honestly.

The bull case (why Bitcoin believers value it):
- Fixed supply: There will only EVER be 21 million Bitcoin in existence. This is hardcoded into the protocol and can't be changed. By comparison, governments can print unlimited dollars.
- Scarcity: Less than 7 billion people in the world could ever own one full Bitcoin (only 21 million exist). Many are already lost forever.
- Decentralization: No government, company, or person controls it.
- 15-year track record: Despite crashes, Bitcoin is up millions of percent since 2009. Early holders who held through every crash are extraordinarily wealthy.
- Institutional adoption: As of 2025, BlackRock, Fidelity, and other major institutions offer Bitcoin ETFs. US Bitcoin spot ETFs launched in January 2024.

The bear case (legitimate concerns):
- Extreme volatility: Bitcoin has crashed 70-80% from peak prices multiple times.
- Regulatory risk: Governments could restrict or tax crypto more heavily.
- No inherent cash flow: Unlike a business (which has earnings) or real estate (which has rent), Bitcoin produces nothing. Its value is entirely based on what the next buyer will pay.
- Environmental concerns: Bitcoin mining uses significant electricity.

The 4-year halving cycle:
Every ~4 years, the amount of new Bitcoin created per block is cut in half (called a "halving"). This happens until the last Bitcoin is mined around 2140. Halvings in 2012, 2016, 2020, and 2024 have historically preceded significant price increases. The April 2024 halving reduced the block reward from 6.25 to 3.125 Bitcoin per block.

Bitcoin reached over $100,000 per coin for the first time in late 2024.

Summary: Bitcoin is a 15-year-old experiment in decentralized money. It might be "digital gold" — a store of value for the digital age. It might also remain highly speculative. Most financial educators suggest treating it as a speculative asset representing no more than 1-5% of an investment portfolio.`,
        takeaway: 'Bitcoin: fixed 21M supply, decentralized, volatile. Reached $100K+ in 2024. Most educators suggest 1-5% of portfolio max if you choose to invest.',
      },
      {
        title: 'Ethereum and Why It Matters',
        content: `If Bitcoin is digital gold — a store of value — Ethereum is something different: it's a programmable blockchain platform. This distinction is crucial.

What does "programmable" mean?
Ethereum allows developers to build applications that run directly on its blockchain. These are called "smart contracts" — programs that automatically execute when certain conditions are met, with no human intermediary needed.

Simple example: Imagine a vending machine. You put in money, press a button, and you get your item — no cashier needed. Smart contracts work the same way but for financial transactions. "When Condition A is met, automatically send Amount B to Person C." All recorded on the blockchain, immutable, trustless.

What gets built on Ethereum?
- DeFi (Decentralized Finance): Lending, borrowing, and trading without banks
- NFTs (Non-Fungible Tokens): Digital ownership certificates for art, music, gaming items
- DAOs (Decentralized Autonomous Organizations): Organizations governed by code and token holders
- Stablecoins: Currencies pegged to the dollar (USDC, DAI) built on Ethereum

Ethereum's upgrade (The Merge, 2022):
Ethereum dramatically reduced its energy consumption by switching from the same "proof of work" system as Bitcoin to "proof of stake." This reduced Ethereum's energy use by approximately 99.95%.

ETH as an investment:
Ethereum is the second largest cryptocurrency by market cap, behind Bitcoin. Unlike Bitcoin's fixed supply, Ethereum's supply can change, but mechanisms exist to reduce it over time. It's also more volatile than Bitcoin.

Key risk: Ethereum faces competition from other smart contract platforms (Solana, Cardano, etc.), though it remains the dominant platform for developer activity.`,
        takeaway: "Ethereum is a programmable blockchain — the foundation for DeFi, NFTs, and decentralized apps. More complex and volatile than Bitcoin.",
      },
      {
        title: 'How to Safely Buy and Store Crypto (Without Getting Scammed)',
        content: `The crypto world has more scams and pitfalls than almost any other investment space. Here's how to protect yourself.

Step 1 — Use a regulated exchange
In the US, the most reputable and regulated exchanges include:
- Coinbase — beginner-friendly, publicly traded, regulated
- Kraken — well-regarded, strong security history
- Gemini — regulated, New York-based, insurance on assets

Avoid: random DeFi platforms you found on Twitter, exchanges you've never heard of, anything promising guaranteed returns or "10x in 30 days."

Step 2 — Enable all security features
Immediately after creating an account:
- Turn on two-factor authentication (use an app like Google Authenticator, not SMS)
- Use a unique, complex password stored in a password manager
- Enable withdrawal address whitelisting if available

Step 3 — Understand cold storage
Exchanges can be hacked or go bankrupt (FTX collapsed in 2022, losing billions of customer funds). For amounts you're serious about keeping long-term, a hardware wallet (like a Ledger or Trezor) stores your crypto offline — completely disconnected from the internet. This is called "cold storage." The crypto world motto: "Not your keys, not your coins."

Step 4 — Crypto is taxable
In the US, every crypto sale, trade, or use is a taxable event. If you buy Bitcoin at $30,000 and sell at $50,000, you owe capital gains tax on $20,000 profit. The IRS has made clear they expect crypto to be reported. Keep records of every transaction. Tax software like CoinTracker or Koinly can help.

Step 5 — Recognize scams
Common crypto scams: celebrity endorsement ads (almost always fake), "recovery agents" who claim they can get back lost crypto (impossible), Ponzi schemes promising guaranteed returns (all frauds), and romance scams where someone asks you to send crypto. If it sounds too good to be true, it is.`,
        takeaway: "Use regulated exchanges (Coinbase, Kraken). Enable all security. Consider hardware wallets for large amounts. Every sale is taxable. Scams are everywhere — be skeptical.",
      },
    ]
  },
  {
    id: 'market',
    icon: '🏛️',
    title: 'How the Market Works',
    tagline: 'Bulls, bears, crashes, and why prices move the way they do.',
    color: '#ec4899',
    lessons: [
      {
        title: 'Understanding Bull and Bear Markets',
        content: `Before we talk about how markets move, two terms you'll hear constantly: bull market and bear market.

Bull Market:
A bull market is a period of rising prices — technically defined as a 20% or greater rise from a recent low. The term comes from how a bull attacks: thrusting its horns upward. In a bull market, investor confidence is high, the economy is typically growing, unemployment is low, and people are generally optimistic.

The US experienced one of the longest bull markets in history from 2009 to 2020 — over 11 years of rising stock prices following the financial crisis.

Bear Market:
A bear market is a period of falling prices — a decline of 20% or more from recent highs. The bear swipes its paws downward. Bear markets are typically accompanied by economic slowdown, rising unemployment, and investor fear.

Notable bear markets in recent history:
- 2000–2002: Dot-com bubble burst. Nasdaq fell ~78% from peak.
- 2007–2009: Financial crisis. S&P 500 fell ~57% from peak.
- 2022: Rate hike bear market. S&P 500 fell ~25% from peak.

The critical fact beginners must internalize: Every single bear market in history has been followed by a bull market that reached new highs. Every one. The people who held through the 2009 bottom and the 2020 crash were richly rewarded. The people who sold at the bottom locked in permanent losses.

Average duration:
- Bull markets last an average of 3.8 years
- Bear markets last an average of 9.6 months

Time is heavily on the side of long-term investors.`,
        takeaway: 'Bull market = prices up 20%+. Bear market = prices down 20%+. Every bear market in history has recovered to new highs. Hold your positions.',
      },
      {
        title: 'Why Stock Prices Go Up and Down Every Single Day',
        content: `New investors are often baffled — sometimes even panicked — by the fact that the market moves every single day, sometimes wildly. Here's the truth: most of those daily moves are noise.

The fundamental driver of stock prices: Expectations

A stock price represents what investors collectively think the company is worth TODAY — based on ALL expected future earnings. This is why a company with no current profits (like many tech startups) can have a high stock price: investors believe it WILL be very profitable in the future.

What causes prices to move moment-to-moment?
New information. Every second, investors are processing new data about a company or the broader economy and adjusting what they think that company is worth.

Types of events that move prices:

Company-specific:
- Earnings reports: "Apple made more profit than expected" → price up. "Less than expected" → price down.
- Product launches, FDA approvals, CEO changes, scandals, mergers
- Guidance: what management says about the future matters enormously

Macroeconomic:
- Jobs report (released monthly): more jobs = stronger economy = stocks up
- CPI inflation data: high inflation → Fed may raise rates → stocks often fall
- Federal Reserve announcements: rate changes move the entire market
- GDP data: confirms whether the economy is growing or shrinking

Geopolitical:
- Wars, political instability, trade disputes
- Tariffs affect companies that import materials or sell internationally

Sentiment/Psychology:
Sometimes markets move on pure fear or greed with little connection to actual business fundamentals. This is where short-term trading becomes nearly indistinguishable from gambling.

The key insight: On any given day, you cannot reliably predict whether the market will be up or down. Over any 20-year period in US history, the market has always been higher. This is why long-term investing works and short-term trading is so difficult.`,
        takeaway: "Prices move on new information about future expectations. Day-to-day moves are mostly noise. Over 20-year periods, the US market has always gone up.",
      },
      {
        title: 'Market Corrections and Crashes — What They Are and How to Survive Them',
        content: `Market corrections are a completely normal, inevitable feature of investing. Understanding this fact can save you from the most common and costly beginner mistake: panic selling.

The official definitions:
- Pullback: A decline of 5–9%. Happens several times per year. Completely normal.
- Correction: A decline of 10–19% from recent highs. Happens roughly every 1–2 years.
- Bear market: A decline of 20%+. Happens every 3–5 years on average.
- Crash: A sudden, dramatic drop — like the 22% single-day drop in 1987, or the 34% drop in March 2020 over 33 days.

What causes crashes?
Usually a combination of: overvalued markets (investors paid too much), a triggering event (a pandemic, a banking failure, a policy change), and then panic as investors sell — which makes others panic and sell — creating a feedback loop.

How long do crashes last?
The fastest major US market crash was in 2020 — the market fell 34% in 33 days due to COVID-19, then fully recovered within 5 months. The 2008-2009 financial crisis was the most severe recent bear market — the S&P 500 fell 57% over 17 months, then took about 4 years to fully recover.

What should you DO during a crash?
1. Do nothing. Seriously. The worst thing you can do is sell at the bottom.
2. If you have spare money, consider buying more. Stocks are on sale.
3. Remember: you don't lose money in a crash unless you sell.

A powerful perspective:
Imagine stocks as a house you own. If the housing market crashes and your house "loses value," you haven't actually lost anything unless you sell. Your house still exists, still has value, and will likely recover. The same is true for quality stocks during downturns.

Investors who kept buying during the 2009 bottom — when fear was at maximum and everyone said the financial system was broken — saw those purchases turn into 10x returns over the next decade.`,
        takeaway: "Corrections (10%+ drops) happen every 1-2 years. Bear markets every 3-5 years. Every single one has fully recovered. Don't sell during crashes — buy if you can.",
      },
      {
        title: 'The Power of Compound Growth — The 8th Wonder of the World',
        content: `Albert Einstein (supposedly) called compound interest "the eighth wonder of the world." Whether or not he said it, the math behind it is genuinely extraordinary.

What is compound growth?
Simple interest: you earn interest only on your original investment.
Compound interest: you earn interest on your original investment PLUS on all the interest you've already earned.

The difference sounds small, but over time it's enormous.

Example with $10,000 at 10% annual return:
- After year 1: $11,000 (earned $1,000)
- After year 2: $12,100 (earned $1,100 — more than year 1!)
- After year 10: $25,937
- After year 20: $67,275
- After year 30: $174,494
- After year 40: $452,593

You put in $10,000. You end up with $452,593. The extra $442,593 came from compound growth — earning returns on your returns.

The Rule of 72:
Here's a mental math shortcut: divide 72 by your expected annual return to find how many years it takes to double your money.
- At 6%: 72 ÷ 6 = 12 years to double
- At 8%: 72 ÷ 8 = 9 years to double
- At 10%: 72 ÷ 10 = 7.2 years to double

Why starting early is so incredibly powerful:
A 22-year-old who invests $5,000/year at 10% until age 32 (just 10 years, then never contributes again) will have MORE at age 65 than a 32-year-old who contributes $5,000/year from 32 to 65 (33 years of investing).

That seems impossible but it's mathematically true. The first $5,000 you invest at 22 has 43 years to compound. The $5,000 you invest at 55 has only 10 years. Start now.`,
        takeaway: 'Compound growth = earning returns on your returns. $10K at 10% becomes $452K in 40 years. The earlier you start, the more dramatic the result.',
      },
      {
        title: 'How to Think About Risk and Build a Portfolio',
        content: `Every investment comes with risk. The goal isn't to eliminate risk — it's to take the right amount of risk for your situation and be compensated for it.

The risk-return relationship:
Higher potential returns generally come with higher risk. A savings account earning 4.5% is very safe — almost zero chance of losing money. Stocks earning an average 10% can drop 40% in a bad year. Cryptocurrency can double or lose 80%. The higher the potential gain, the higher the potential pain.

Three main types of investments by risk level:

Low risk: Savings accounts, money market accounts, US Treasury bonds, CDs. Current high-yield savings accounts offer around 3.5–4.1% as of 2026. Safe but barely beats inflation over long periods.

Medium risk: Corporate bonds, dividend stocks, real estate investment trusts (REITs), balanced funds. Return potential: 5-8%/year average. Some volatility but relatively stable.

High risk: Growth stocks, small-cap stocks, international emerging markets, cryptocurrency. Return potential: 10%+/year but with significant year-to-year volatility.

Diversification — Don't put all your eggs in one basket:
If you own 500 different stocks (via an S&P 500 index fund) and one company fails, you barely notice. If you put all your money in one stock and it fails, you lose everything. Diversification is the closest thing to a free lunch in investing.

Asset allocation — What mix is right for you?
General rule of thumb (not advice): Younger investors with decades to invest can afford more stocks. Older investors approaching retirement want more stable assets.

A simple classic allocation:
- 20s-30s: 90% stocks, 10% bonds
- 40s: 80% stocks, 20% bonds
- 50s: 70% stocks, 30% bonds
- At retirement: 50-60% stocks, 40-50% bonds

Target-date funds automatically do this rebalancing for you.`,
        takeaway: 'Higher potential returns = higher risk. Diversify across many assets. Asset allocation shifts more conservative as you age. Index funds handle this automatically.',
      },
    ]
  },
  {
    id: 'economy',
    icon: '🌐',
    title: 'Economy Basics',
    tagline: 'GDP, inflation, the Fed, interest rates — what they mean for YOUR money.',
    color: '#06b6d4',
    lessons: [
      {
        title: 'What Is the Economy? (And Why Should You Care?)',
        content: `The word "economy" gets thrown around constantly in news and politics. But what does it actually mean, and why should someone who just wants to manage their finances care?

The economy, simply put, is the sum of all economic activity in a country: all the things people buy and sell, the jobs people work, the money companies make, and the money governments spend. It's not one thing — it's millions of individual decisions that, together, form patterns we can measure and analyze.

Why does it matter to YOUR personal finances?
Because the economy affects nearly every aspect of your financial life:

- Job security: When the economy is growing, companies hire. When it contracts, layoffs increase.
- Wages: Economic growth creates competition for workers, pushing wages up.
- Interest rates: Economic conditions influence whether your mortgage, car loan, or credit card rates go up or down.
- Investment returns: Your 401(k) and investments rise and fall with the broader economy.
- Inflation: The economy determines how fast prices rise — and whether your money buys more or less over time.
- Housing: Economic conditions drive home prices, rental costs, and mortgage rates.

In short: you don't need to become an economist. But understanding a few key concepts — GDP, inflation, interest rates, and the Federal Reserve — will help you make smarter decisions with your money.

The three big questions about the economy:
1. Is it growing or shrinking? (GDP)
2. Are prices rising too fast? (Inflation)
3. What is the government doing about it? (Federal Reserve policy)

These three questions drive the news cycle, the stock market, and your financial reality. Let's break each one down.`,
        takeaway: 'The economy affects your job, wages, interest rates, investments, and the cost of everything you buy. Understanding a few key concepts is genuinely valuable.',
      },
      {
        title: 'GDP — The Economy\'s Report Card',
        content: `GDP stands for Gross Domestic Product. It's the total value of all goods and services produced in a country during a specific period — usually measured quarterly and annually.

Think of it as the economy's report card. A rising GDP = the economy is growing. A falling GDP = the economy is shrinking.

What goes into GDP?
Economists measure it as the sum of:
1. Consumer spending: What people buy — groceries, cars, haircuts (the biggest piece, about 70% of US GDP)
2. Business investment: Equipment, factories, technology
3. Government spending: Roads, military, schools
4. Net exports: What we sell to other countries minus what we import

Why does GDP matter to you?
When GDP is growing at a healthy rate (around 2-3% annually for the US):
- Companies are making more money → stock prices tend to rise
- Employers are hiring → wages rise, unemployment falls
- People feel confident → they spend more, fueling more growth

When GDP is shrinking (two consecutive quarters of negative GDP = a recession):
- Companies cut costs → layoffs
- Unemployment rises → people spend less
- Stock market often falls (though it can fall in anticipation, before a recession officially begins)

Real GDP vs. Nominal GDP:
"Nominal" GDP includes inflation. "Real" GDP adjusts for inflation to show true economic growth. If the economy "grew" 5% but inflation was 4%, real growth was only 1%.

2024-2025 context: The US economy showed resilience through the post-COVID inflation period. Despite aggressive Federal Reserve rate hikes in 2022-2023, the US avoided a recession that many economists predicted — a so-called "soft landing." GDP growth remained positive.`,
        takeaway: "GDP measures total economic output. Growing GDP = more jobs, higher wages, rising stocks. Two quarters of shrinking GDP = recession.",
      },
      {
        title: 'Inflation — The Silent Thief of Your Savings',
        content: `Inflation is the rate at which the general price level of goods and services rises over time. If inflation is 3%, then something that cost $100 last year costs $103 this year.

That might not sound like much. But here's the devastating compounding effect: at 3% inflation, prices double approximately every 24 years. At 7% inflation (like the US experienced in 2022), prices double every 10 years.

How is inflation measured?
The CPI (Consumer Price Index) is the most commonly cited measure. The US Bureau of Labor Statistics tracks the prices of a "basket" of about 80,000 goods and services — groceries, gasoline, housing, healthcare, clothing — and calculates how much that basket costs month over month.

The PCE (Personal Consumption Expenditure) index is the Federal Reserve's preferred measure of inflation because it adjusts for consumer behavior changes (when beef gets expensive, people buy chicken).

Recent real-world context:
- 2020: COVID disrupted supply chains globally
- 2021: As the economy reopened, massive government stimulus + supply shortages = inflation began rising
- 2022: US inflation hit 9.1% — a 40-year high. A family spending $3,000/month was effectively spending $327 more per month than the year before just to buy the same things.
- 2023-2024: The Federal Reserve raised rates aggressively to fight inflation. It worked — inflation cooled to around 2.5-3% range by 2024-2025.

Why is 2% inflation the target?
The Federal Reserve aims for approximately 2% annual inflation. A little inflation is healthy — it encourages spending (why buy today if prices will be lower tomorrow?), gives businesses room to cut wages without cutting nominal pay, and allows the economy to function smoothly. Zero inflation or deflation (falling prices) sounds nice but is actually dangerous for growth.

What inflation does to your savings:
If your savings account earns 1% but inflation is 3%, your money loses purchasing power every year. In real terms, you're getting poorer. This is why money sitting under a mattress (or in a 0% checking account) is a guaranteed way to lose wealth.`,
        takeaway: 'Inflation erodes purchasing power. 2022 US inflation hit 9.1% — a 40-year high. Money in low-yield accounts loses real value. Investing is how you stay ahead of inflation.',
      },
      {
        title: 'The Federal Reserve — Who Actually Controls Your Interest Rates',
        content: `The Federal Reserve (the "Fed") is the central bank of the United States. It's one of the most powerful financial institutions on the planet. When the Fed speaks, global markets move.

What is a central bank?
Every major economy has a central bank. It's a government-affiliated institution that manages the country's money supply and monetary policy. It is NOT where you open a checking account — it's the bank for banks.

The Fed's two official mandates (from Congress):
1. Maximize employment (keep unemployment low)
2. Maintain price stability (keep inflation around 2%)

These two goals often pull in opposite directions. When inflation is high, you slow the economy (accepting some unemployment). When unemployment is high, you stimulate the economy (accepting some inflation).

The Fed's main tool: The Federal Funds Rate
This is the interest rate at which banks lend money to each other overnight. It sounds obscure, but it ripples through the ENTIRE economy:

When the Fed RAISES rates:
→ Banks pay more to borrow from each other
→ Banks charge more to lend to businesses and consumers
→ Your mortgage, car loan, and credit card rates go UP
→ Business investment slows (loans are expensive)
→ Consumer spending falls (credit costs more)
→ Economy cools → Inflation falls
→ Stock prices often drop (higher rates = competition from bonds, and future earnings worth less)

When the Fed CUTS rates:
→ Borrowing gets cheaper across the board
→ Consumer spending and business investment pick up
→ Economy heats up
→ Stock prices often rise
→ But prolonged low rates can fuel inflation

Recent history:
2022-2023: The Fed raised rates 11 times, bringing the federal funds rate from near-zero to 5.25-5.5% — the highest in 23 years. This successfully brought inflation down but made mortgages and credit significantly more expensive.
2024-2025: The Fed began cutting rates — 1% in the second half of 2024 plus additional cuts in 2025 — as inflation cooled.`,
        takeaway: "The Fed sets interest rates that affect every loan and investment. Raises rates to fight inflation; cuts rates to stimulate growth. Following Fed decisions is essential financial literacy.",
      },
      {
        title: 'Interest Rates and What They Mean for Your Real Life',
        content: `Few things affect your daily financial life more directly than interest rates. Here's exactly how rate changes touch each area:

Mortgage Rates:
In 2020-2021, with rates near zero, a 30-year fixed mortgage averaged around 3%. In 2023, after the Fed's hikes, that same mortgage hit over 8%. On a $350,000 home:
- At 3%: ~$1,476/month
- At 8%: ~$2,568/month
That's $1,092 per month more for the exact same house.

This is why higher rates dramatically slow the housing market — many potential buyers simply can't afford the payments.

Credit Cards:
Most credit cards have variable rates tied to the prime rate (which follows the federal funds rate). As of 2025, the average credit card APR is around 20-22%. On a $5,000 balance with minimum payments, at 20% APR you'll pay roughly $1,700+ in interest over time. High-interest credit card debt is one of the most financially destructive forces for everyday households.

Car Loans:
In 2020-2021, auto loan rates were around 3-4%. By 2023-2024, they rose to 7-9%+. On a $35,000 car financed over 60 months:
- At 4%: ~$645/month, ~$3,700 in interest
- At 8%: ~$709/month, ~$7,540 in interest
The same car costs $3,840 more in interest just because rates are higher.

Savings Accounts and CDs:
This is the silver lining of high rates. In 2020-2021, the best high-yield savings accounts paid around 0.5%. By 2023-2025, many online high-yield savings accounts and CDs offered 4.5-5.0%+. If you have an emergency fund sitting in a big bank earning 0.01%, you're missing out significantly.

Student Loans:
New federal student loan interest rates reset every July 1 based on the 10-year Treasury note. Rising rates mean newer loans are more expensive.

Bottom line: Interest rates are the price of money. When money is cheap (low rates), borrow and invest. When money is expensive (high rates), prioritize paying down debt and take advantage of higher savings rates.`,
        takeaway: 'Interest rates affect mortgages, car loans, credit cards, and savings rates directly. Higher rates = more expensive debt but better savings returns. Move emergency funds to high-yield accounts.',
      },
      {
        title: 'How to Read Economic News Without Panicking',
        content: `Turn on any financial news channel and you'll hear terms like "the Fed might raise rates," "jobs numbers came in hot," or "inflation fears rattle markets." This can feel overwhelming. Here's a simple framework.

The 5 most important economic reports to know:

1. Jobs Report (released first Friday of every month)
Official name: "Employment Situation Summary" from the Bureau of Labor Statistics. Reports the unemployment rate and how many jobs were added or lost. A strong jobs report (lots of new jobs) often means economic growth, which can be good for stocks — OR it means inflation pressure, which might make the Fed raise rates. Markets often react in seemingly paradoxical ways.

2. CPI Report (Consumer Price Index) — released monthly
Shows whether inflation is rising or falling. Higher than expected CPI = stocks often fall (fear of rate hikes). Lower than expected = stocks often rise.

3. Federal Reserve Meetings (8 times per year)
The Federal Open Market Committee (FOMC) meets 8 times per year. After each meeting, they announce whether they're raising, cutting, or holding interest rates. Every Fed meeting moves markets.

4. GDP Report — released quarterly
Shows whether the overall economy grew or shrank. Two consecutive quarters of negative GDP growth = recession.

5. Earnings Season (4 times per year)
Major companies release quarterly earnings reports. If companies are beating expectations, markets tend to rise. If they're missing, markets often fall.

How to avoid panic:
Most financial news is short-term noise designed to keep you watching. The headlines that say "Markets in turmoil" or "Stocks soar" are describing a single day's movement. They are largely irrelevant to your long-term financial plan.

The filter: Ask yourself — "Does this news change my expectations for the economy 10-20 years from now?" If no, it probably doesn't require action.

The discipline of holding to your long-term plan through short-term volatility is the single most valuable skill a retail investor can develop.`,
        takeaway: 'Key reports: Jobs (monthly), CPI (monthly), Fed meetings (8x/year), GDP (quarterly), Earnings (quarterly). Most news is noise. Long-term plan > short-term headlines.',
      },
    ]
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────────────────────

function CourseCard({ course, onClick }) {
  return (
    <Link to={`/courses/${course.id}`} style={{ ...styles.card, textDecoration: 'none' }}>
      <div style={{ ...styles.cardAccent, background: course.color + '22', borderColor: course.color + '44' }}>
        <span style={styles.cardIcon}>{course.icon}</span>
      </div>
      <div style={styles.cardBody}>
        <h3 style={styles.cardTitle}>{course.title}</h3>
        <p style={styles.cardTagline}>{course.tagline}</p>
        <div style={styles.cardMeta}>{course.lessons.length} lessons</div>
      </div>
      <span style={{ ...styles.cardCta, color: course.color }}>Start →</span>
    </Link>
  );
}

function QuizBox({ lesson }) {
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [questions, setQuestions] = useState([]);
  const [selected, setSelected] = useState({}); // { [qIdx]: choiceIdx }
  const [submitted, setSubmitted] = useState(false);

  const loadQuiz = async () => {
    if (!canAskAI()) {
      setStatus('error');
      return;
    }
    incrementAICount();
    setStatus('loading');
    setSelected({});
    setSubmitted(false);
    try {
      const res = await axios.post(`${API}/api/quiz`, {
        title: lesson.title,
        takeaway: lesson.takeaway,
        content: lesson.content,
      }, { timeout: 60000 });
      if (res.data.questions && res.data.questions.length > 0) {
        setQuestions(res.data.questions);
        setStatus('ready');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const score = submitted
    ? questions.filter((q, i) => selected[i] === q.answer).length
    : 0;

  return (
    <div style={qStyles.wrap}>
      {status === 'idle' && (
        <button onClick={loadQuiz} style={qStyles.startBtn}>
          🧠 Test Your Knowledge
        </button>
      )}
      {status === 'loading' && (
        <div style={qStyles.loading}>⏳ Generating quiz questions...</div>
      )}
      {status === 'error' && (
        <div style={qStyles.error}>
          {!canAskAI() && !isPro()
            ? <>You've used your free AI questions for today. <Link to="/signup?plan=pro" style={qStyles.retry}>Upgrade to Pro →</Link></>
            : <>Couldn't generate quiz right now. <span onClick={loadQuiz} style={qStyles.retry}>Try again →</span></>
          }
        </div>
      )}
      {status === 'ready' && (
        <div>
          <div style={qStyles.title}>🧠 Quick Quiz — Test What You Learned</div>
          {questions.map((q, qi) => (
            <div key={qi} style={qStyles.question}>
              <div style={qStyles.qText}>{qi + 1}. {q.q}</div>
              <div style={qStyles.options}>
                {q.options.map((opt, oi) => {
                  let bg = '#1e293b';
                  let border = '#334155';
                  let color = '#cbd5e1';
                  if (submitted) {
                    if (oi === q.answer) { bg = '#14532d'; border = '#22c55e'; color = '#86efac'; }
                    else if (selected[qi] === oi && oi !== q.answer) { bg = '#450a0a'; border = '#ef4444'; color = '#fca5a5'; }
                  } else if (selected[qi] === oi) {
                    bg = '#1e3a5f'; border = '#38bdf8'; color = '#e0f2fe';
                  }
                  return (
                    <button
                      key={oi}
                      onClick={() => !submitted && setSelected(s => ({ ...s, [qi]: oi }))}
                      style={{ ...qStyles.option, background: bg, border: `1px solid ${border}`, color }}
                    >
                      <span style={qStyles.optLabel}>{['A','B','C','D'][oi]}</span>
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {!submitted ? (
            <button
              onClick={() => Object.keys(selected).length === questions.length && setSubmitted(true)}
              style={{ ...qStyles.submitBtn, opacity: Object.keys(selected).length === questions.length ? 1 : 0.4 }}
            >
              Submit Answers →
            </button>
          ) : (
            <div style={qStyles.result}>
              {score === questions.length
                ? `🎉 Perfect score! ${score}/${questions.length} — you nailed it.`
                : score >= Math.ceil(questions.length / 2)
                ? `✅ Good job! ${score}/${questions.length} — solid understanding.`
                : `📚 ${score}/${questions.length} — re-read the lesson and try again.`}
              <span
                onClick={() => { setStatus('idle'); setQuestions([]); setSelected({}); setSubmitted(false); }}
                style={qStyles.retry}
              > Try again →</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const qStyles = {
  wrap: { marginTop: '24px', borderTop: '1px solid #1e293b', paddingTop: '20px' },
  startBtn: { background: 'linear-gradient(135deg, #0ea5e9, #6366f1)', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', letterSpacing: '0.3px' },
  loading: { color: '#94a3b8', fontSize: '14px', padding: '12px 0' },
  error: { color: '#f87171', fontSize: '14px', padding: '12px 0' },
  retry: { color: '#38bdf8', cursor: 'pointer', textDecoration: 'underline', marginLeft: '6px' },
  title: { color: '#e2e8f0', fontWeight: '700', fontSize: '15px', marginBottom: '16px' },
  question: { marginBottom: '20px' },
  qText: { color: '#cbd5e1', fontSize: '14px', fontWeight: '600', marginBottom: '10px', lineHeight: '1.5' },
  options: { display: 'flex', flexDirection: 'column', gap: '8px' },
  option: { display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s' },
  optLabel: { fontWeight: '700', minWidth: '18px', fontSize: '12px', opacity: 0.7 },
  submitBtn: { marginTop: '8px', background: '#0ea5e9', color: '#fff', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  result: { marginTop: '16px', padding: '14px 16px', background: '#0f172a', borderRadius: '8px', color: '#e2e8f0', fontSize: '14px', fontWeight: '600', border: '1px solid #1e293b' },
};

function LessonItem({ lesson, index, isOpen, onToggle }) {
  return (
    <div style={styles.lessonWrap}>
      <button onClick={onToggle} style={styles.lessonBtn}>
        <div style={styles.lessonBtnLeft}>
          <span style={styles.lessonNum}>{index + 1}</span>
          <span style={styles.lessonTitle}>{lesson.title}</span>
        </div>
        <span style={styles.toggle}>{isOpen ? '▲' : '▼'}</span>
      </button>
      {isOpen && (
        <div style={styles.lessonBody}>
          {lesson.content.split('\n\n').map((para, j) => (
            <p key={j} style={styles.para}>{para}</p>
          ))}
          {lesson.takeaway && (
            <div style={styles.takeawayBox}>
              <span style={styles.takeawayLabel}>💡 Key Takeaway</span>
              <p style={styles.takeawayText}>{lesson.takeaway}</p>
            </div>
          )}
          <QuizBox lesson={lesson} />
        </div>
      )}
    </div>
  );
}

function AIBox({ courseTitle }) {
  const [q, setQ] = useState('');
  const [a, setA] = useState('');
  const [loading, setLoading] = useState(false);

  const ask = async () => {
    if (!q.trim()) return;
    if (!canAskAI()) {
      setA("You've used your 5 free AI uses for today. Upgrade to Pro for more.");
      return;
    }
    incrementAICount();
    setLoading(true); setA('');
    try {
      const response = await fetch(`${API}/api/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: q, context: courseTitle }),
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      setLoading(false);
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        setA(prev => prev + decoder.decode(value, { stream: true }));
      }
    } catch {
      setA('AI unavailable right now. Try again later.');
      setLoading(false);
    }
  };

  return (
    <div style={styles.aiBox}>
      <div style={styles.aiHead}>
        <span style={styles.aiEmoji}>🤖</span>
        <div>
          <h3 style={styles.aiTitle}>Have a question about {courseTitle}?</h3>
          <p style={styles.aiSub}>Ask the AI — get a plain English answer instantly, no judgment.</p>
        </div>
      </div>
      <input
        style={styles.aiInput}
        placeholder={`Ask anything about ${courseTitle}...`}
        value={q}
        onChange={e => setQ(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && ask()}
      />
      <button onClick={ask} disabled={loading} style={{ ...styles.aiBtn, opacity: loading ? 0.6 : 1 }}>
        {loading ? 'Thinking...' : 'Ask KnowFi AI →'}
      </button>
      {a && (
        <div style={styles.aiAnswer}>
          <div style={styles.aiAnswerLabel}>🤖 KnowFi AI:</div>
          <div style={styles.aiAnswerText}>{a}</div>
        </div>
      )}
    </div>
  );
}

export function CourseDetail({ course, onBack, pro }) {
  const [openLesson, setOpenLesson] = useState(0);

  return (
    <div>
      <button onClick={onBack} style={styles.backBtn}>← All Courses</button>

      <div style={styles.detailHeader}>
        <div style={{ ...styles.detailIconWrap, background: course.color + '18', borderColor: course.color + '33' }}>
          <span style={styles.detailIcon}>{course.icon}</span>
        </div>
        <div>
          <h1 style={styles.detailTitle}>{course.title}</h1>
          <p style={styles.detailTagline}>{course.tagline}</p>
          <div style={styles.detailMeta}>{course.lessons.length} lessons · 100% free · Beginner friendly · AI Q&A + Quizzes included</div>
        </div>
      </div>

      <div style={styles.lessonsSection}>
        {course.lessons.map((lesson, i) => (
          <LessonItem
            key={i}
            lesson={lesson}
            index={i}
            isOpen={openLesson === i}
            onToggle={() => setOpenLesson(openLesson === i ? null : i)}
          />
        ))}
      </div>

      <AIBox courseTitle={course.title} />

      <div style={styles.disclaimer}>
        Content is educational only. Not financial or investment advice. Always consult a qualified financial advisor for personal decisions. Contribution limits and tax rules change — verify current figures at IRS.gov.
      </div>
    </div>
  );
}

function Courses() {
  const [selected, setSelected] = useState(null);
  const pro = !!(localStorage.getItem('knowfi_user') && JSON.parse(localStorage.getItem('knowfi_user') || '{}').plan === 'pro');

  return (
    <div style={styles.page}>
      <SEO
        title="Free Personal Finance Courses — Learn Money Without the Jargon"
        description="Six completely free courses covering investing, budgeting, retirement, crypto, and more. Plain English lessons with quizzes and AI Q&A. No Wall Street nonsense."
        path="/courses"
        keywords="free personal finance course, investing course, budgeting course, retirement planning, crypto explained"
      />
      <div style={styles.glow} />
      {!selected ? (
        <>
          <div style={styles.header}>
            <h1 style={styles.pageTitle}>Finance Courses</h1>
            <p style={styles.pageSub}>Real financial education. Written for people starting from zero. No fluff, no jargon.</p>
          </div>

          <div style={styles.grid}>
            {COURSES.map(c => <CourseCard key={c.id} course={c} onClick={setSelected} />)}
          </div>
          <div style={styles.bottomNote}>
            📌 Each course includes detailed lessons and an AI question box so you can ask follow-up questions while you learn.
          </div>
        </>
      ) : (
        <CourseDetail course={selected} onBack={() => setSelected(null)} pro={pro} />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const styles = {
  page: { background: '#0f172a', color: '#f8fafc', minHeight: '100vh', padding: '60px 40px', maxWidth: '960px', margin: '0 auto', position: 'relative' },
  glow: { position: 'absolute', top: '-60px', left: '50%', transform: 'translateX(-50%)', width: '700px', height: '500px', background: 'radial-gradient(ellipse at center, rgba(56,189,248,0.07) 0%, transparent 60%)', pointerEvents: 'none', zIndex: 0 },
  header: { position: 'relative', zIndex: 1, marginBottom: '40px' },
  pageTitle: { fontSize: '40px', fontWeight: '800', marginBottom: '8px', letterSpacing: '-0.5px' },
  pageSub: { color: '#64748b', fontSize: '16px', margin: 0 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', position: 'relative', zIndex: 1 },

  card: { background: '#1e293b', border: '1px solid #334155', borderRadius: '16px', padding: '24px', textAlign: 'left', cursor: 'pointer', color: '#f8fafc', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', gap: '12px', width: '100%', boxSizing: 'border-box' },
  cardAccent: { width: '52px', height: '52px', borderRadius: '12px', border: '1px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  cardIcon: { fontSize: '26px' },
  cardBody: { flex: 1 },
  cardTitle: { fontSize: '17px', fontWeight: '700', margin: '0 0 6px', color: '#f1f5f9' },
  cardTagline: { color: '#64748b', fontSize: '13px', lineHeight: 1.5, margin: '0 0 10px' },
  cardMeta: { color: '#475569', fontSize: '12px' },
  cardCta: { fontSize: '14px', fontWeight: '600' },

  bottomNote: { position: 'relative', zIndex: 1, marginTop: '28px', color: '#475569', fontSize: '14px', textAlign: 'center' },

  backBtn: { background: 'transparent', border: 'none', color: '#38bdf8', fontSize: '14px', cursor: 'pointer', padding: '0 0 20px', fontFamily: 'inherit', display: 'block', position: 'relative', zIndex: 1 },
  detailHeader: { display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '40px', position: 'relative', zIndex: 1 },
  detailIconWrap: { width: '60px', height: '60px', borderRadius: '14px', border: '1px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  detailIcon: { fontSize: '30px' },
  detailTitle: { fontSize: '32px', fontWeight: '800', margin: '0 0 6px', letterSpacing: '-0.3px' },
  detailTagline: { color: '#64748b', fontSize: '15px', margin: '0 0 8px' },
  detailMeta: { color: '#475569', fontSize: '12px' },

  lessonsSection: { position: 'relative', zIndex: 1, marginBottom: '32px' },
  lessonWrap: { borderBottom: '1px solid #1e293b' },
  lessonBtn: { width: '100%', background: 'transparent', border: 'none', color: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '18px 0', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
  lessonBtnLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  lessonNum: { width: '28px', height: '28px', background: 'rgba(56,189,248,0.1)', border: '1px solid rgba(56,189,248,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', color: '#38bdf8', flexShrink: 0 },
  lessonTitle: { fontSize: '15px', fontWeight: '600', lineHeight: 1.3 },
  toggle: { color: '#475569', fontSize: '11px', flexShrink: 0 },

  lessonBody: { padding: '4px 0 28px 42px' },
  para: { color: '#94a3b8', lineHeight: 1.85, fontSize: '15px', marginBottom: '16px', margin: '0 0 16px' },
  takeawayBox: { background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '10px', padding: '14px 18px', marginTop: '16px' },
  takeawayLabel: { display: 'block', fontSize: '11px', color: '#38bdf8', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' },
  takeawayText: { color: '#e2e8f0', fontSize: '14px', lineHeight: 1.6, margin: 0, fontWeight: '500' },

  aiBox: { background: '#1e293b', borderRadius: '16px', padding: '28px', border: '1px solid rgba(56,189,248,0.2)', position: 'relative', zIndex: 1, marginBottom: '24px' },
  aiHead: { display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '20px' },
  aiEmoji: { fontSize: '28px', flexShrink: 0 },
  aiTitle: { fontSize: '18px', fontWeight: '700', margin: '0 0 4px', color: '#f1f5f9' },
  aiSub: { color: '#64748b', fontSize: '14px', margin: 0 },
  aiInput: { width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f8fafc', padding: '12px 14px', fontSize: '15px', boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: '12px', outline: 'none' },
  aiBtn: { background: '#38bdf8', color: '#0f172a', border: 'none', padding: '12px 24px', borderRadius: '10px', fontWeight: '700', fontSize: '15px', cursor: 'pointer', fontFamily: 'inherit' },
  aiAnswer: { marginTop: '20px', background: '#0f172a', borderRadius: '10px', padding: '18px', border: '1px solid #334155' },
  aiAnswerLabel: { color: '#38bdf8', fontSize: '11px', fontWeight: '700', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '10px', display: 'block' },
  aiAnswerText: { color: '#e2e8f0', lineHeight: 1.8, fontSize: '15px', whiteSpace: 'pre-wrap', margin: 0 },

  disclaimer: { color: '#334155', fontSize: '12px', lineHeight: 1.6, textAlign: 'center', position: 'relative', zIndex: 1 },
  freeBar: { background: 'rgba(56,189,248,0.06)', border: '1px solid rgba(56,189,248,0.15)', borderRadius: '10px', padding: '12px 16px', marginBottom: '20px', fontSize: '14px', color: '#64748b', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px', position: 'relative', zIndex: 1 },
  freeBarLink: { color: '#38bdf8', textDecoration: 'none', fontWeight: '600', fontSize: '14px' },
  lockedLesson: { display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 0', borderBottom: '1px solid #1e293b' },
  unlockLink: { marginLeft: 'auto', color: '#38bdf8', fontSize: '13px', fontWeight: '600', textDecoration: 'none' },
  aiGate: { background: '#1e293b', borderRadius: '16px', padding: '24px', border: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '24px', position: 'relative', zIndex: 1 },
  aiGateIcon: { fontSize: '32px', flexShrink: 0 },
  aiGateTitle: { fontSize: '17px', fontWeight: '700', margin: '0 0 4px', color: '#f1f5f9' },
  aiGateDesc: { color: '#64748b', fontSize: '14px', margin: 0 },
  aiGateBtn: { marginLeft: 'auto', background: '#38bdf8', color: '#0f172a', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', fontSize: '14px', textDecoration: 'none', whiteSpace: 'nowrap' },
};

export default Courses;
